import { camelCase } from 'lodash';
import { v4 } from 'uuid';

import {
  type AISuggestionResponse,
  type FieldSchema,
  type FieldSuggestion,
} from '../types';

// Helper function to map API field names to target field names
const mapAPIFieldToTargetField = (
  apiFieldName: string,
  targetFields: Array<FieldSchema>,
): string => {
  // Try direct match first
  const directMatch = targetFields.find(
    (field) => field.field === apiFieldName,
  );
  if (directMatch) {
    return apiFieldName;
  }

  // Try mapping from underscore to bracket notation (event_title -> event[title])
  if (apiFieldName.includes('_')) {
    const parts = apiFieldName.split('_');
    if (parts.length >= 2) {
      const parent = parts[0];
      const child = parts.slice(1).join('_');
      const bracketNotation = `${parent}[${child}]`;
      const bracketMatch = targetFields.find(
        (field) => field.field === bracketNotation,
      );
      if (bracketMatch) {
        return bracketNotation;
      }
    }
  }

  // Try mapping from underscore to dot notation (event_title -> event.title)
  if (apiFieldName.includes('_')) {
    const parts = apiFieldName.split('_');
    if (parts.length >= 2) {
      const parent = parts[0];
      const child = parts.slice(1).join('_');
      const dotNotation = `${parent}.${child}`;
      const dotMatch = targetFields.find(
        (field) => field.field === dotNotation,
      );
      if (dotMatch) {
        return dotNotation;
      }
    }
  }

  // Fallback to camelCase
  return camelCase(apiFieldName);
};

/**
 * Maps API suggestion response to internal FieldSuggestion object
 *
 * Transforms raw API responses into complete suggestion objects with:
 * - Unique IDs and timestamps
 * - Field name normalization (camelCase)
 * - Initial history tracking
 * - Status management
 *
 * @param suggestion - Raw API response from AI service
 * @param targetFields - Available field schemas for validation
 * @param targetData - Current form data for context
 * @returns Complete FieldSuggestion object with metadata
 */
export const mapAPIResponseToSuggestion = (
  suggestion: AISuggestionResponse,
  targetFields: Array<FieldSchema>,
  targetData: Record<string, any>,
): FieldSuggestion => {
  const fieldName = mapAPIFieldToTargetField(
    suggestion.fieldName,
    targetFields,
  );

  const fieldSchema = targetFields.find((field) => field.field === fieldName);

  return {
    ...suggestion,
    fieldName,
    id: v4(),
    schema: fieldSchema as FieldSchema,
    currentValue: targetData[suggestion.fieldName] || '',
    status: 'pending' as const,
    history: [
      {
        id: v4(),
        suggestedValue: suggestion.suggestedValue,
        reasoning: suggestion.reasoning,
        timestamp: Date.now(),
      },
    ],
    currentHistoryIndex: 0,
  };
};
